<template>
  <j-modal
    :title="title"
    :width="width"
    :visible="visible"
    :maskClosable="false"
    :forceRender="true"
    :style="modalStyle"
    fullscreen
    switchFullscreen
    @cancel="handleCancel"
    wrapClassName="ant-modal-cust-warp">

    <!-- 顶部工具栏 - 仅在预览模式显示 -->
    <div v-if="!designerVisible" class="top-toolbar">
      <div class="toolbar-left" style="display:flex; align-items:center; gap:8px;">
        <a-button key="templateSetting" icon="setting" @click="handleTemplateSetting">模板设置</a-button>
        <a-button key="printPreview" type="primary" icon="printer" @click="handlePrintPreview">打印预览</a-button>
      </div>
    </div>

    <template slot="footer">
      <div class="footer-bar">
        <div class="footer-right">
          <a-button @click="handleCancel">取消</a-button>
        </div>
      </div>
    </template>

    <!-- 预览模式 -->
    <a-spin v-if="!designerVisible" :spinning="loading" tip="正在加载送货单...">
      <div class="hiprint-container">
        <div v-if="!templateJson && !loading" class="no-template">
          <a-empty description="暂无打印模板，请先执行 print_shd.sql 并点击模板设置" />
        </div>
        <div v-else class="preview-wrapper">
          <div
            ref="printContent"
            class="print-content"
            :style="{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center' }">
          </div>
        </div>
      </div>
    </a-spin>

    <!-- 设计器模式 - 直接嵌入当前模态框 -->
    <div v-if="designerVisible" class="designer-wrapper">
      <EmbeddedDesigner
        ref="embeddedDesigner"
        :billType="billTypeCode"
        :billTypeName="billTypeName"
        :billNo="currentBillNo"
        :templateId="selectedTemplateId"
        :billData="printData"
        @close="onDesignerClose"
        @saved="onDesignerSaved"
      />
    </div>
  </j-modal>
</template>

<script>
/**
 * 收单中心-送货单打印（SHD，order-receive-plugin）
 *
 * 数据源：XSDD 关联出库单（XSCK）+ 出库二维码（getScanQrCode）。
 * 模板：jsh_print_template bill_type='SHD' 两级加载（租户模板 → 系统默认 tenant_id=0，
 * 系统默认模板由插件 print_shd.sql 初始化）。
 * 二维码内容 qrContent（JSHOR|XSCK单号|防伪token）由客户 H5「扫码入库」识别，
 * 自动生成客户租户采购入库单（ScanInboundService.scanInbound）。
 */
import {
  getList,
  getDepotItemDetailList,
  getDepotHeadByNumber,
  getCurrentSystemInfo,
  loadTemplateWithFallback
} from '@/api/plugin/print-template'
import HiprintService from '@/services/hiprint-service'
import EmbeddedDesigner from '@/views/plugin/print-template/EmbeddedDesigner.vue'
import { getScanQrCode } from '@/api/plugin/orderReceive'

export default {
  name: 'OrderCenterPrint',
  components: { EmbeddedDesigner },
  data() {
    return {
      title: '送货单打印',
      width: '1000px',
      visible: false,
      loading: false,
      modalStyle: 'top:20px;height: 95%;',
      zoomLevel: 90,
      billTypeCode: 'SHD',
      billTypeName: '送货单',
      currentBillNo: '',
      selectedTemplateId: null,
      templateJson: null,
      printData: {},
      designerVisible: false
    }
  },
  methods: {
    /**
     * 显示送货单打印窗口
     * @param {Object} record 收单中心行记录（需含 headId=XSDD头ID、billNo）
     */
    async show(record) {
      this.visible = true
      this.designerVisible = false
      this.loading = true
      this.templateJson = null
      this.printData = {}
      this.currentBillNo = record.billNo || ''
      this.title = '送货单打印 - ' + this.currentBillNo
      try {
        await this.loadData(record)
        await this.loadTemplateList()
        await this.loadAndRender()
      } catch (error) {
        console.error('[OrderCenterPrint] 加载送货单失败:', error)
        this.$message.warning(error.message || '加载送货单失败')
      } finally {
        this.loading = false
      }
    },
    /** 加载出库二维码 + XSCK 单据主体/明细 + 公司信息 */
    async loadData(record) {
      // 1. 出库二维码（含 XSCK 单号/头ID/二维码内容；未出库时后端报错提示）
      const qrRes = await getScanQrCode(record.headId)
      if (!qrRes.success) {
        throw new Error(qrRes.message || '获取出库二维码失败')
      }
      const qrData = qrRes.data || {}
      // 2. XSCK 主体 + 公司信息（并行）
      const [headRes, systemRes] = await Promise.all([
        getDepotHeadByNumber(qrData.xsckBillNo),
        getCurrentSystemInfo()
      ])
      if (!headRes || headRes.code !== 200 || !headRes.data) {
        throw new Error('出库单信息获取失败：' + qrData.xsckBillNo)
      }
      const head = headRes.data
      // 3. XSCK 明细
      let detailRows = []
      if (qrData.xsckHeadId) {
        const detailRes = await getDepotItemDetailList({
          headerId: qrData.xsckHeadId,
          mpList: '扩展1,扩展2,扩展3'
        })
        if (detailRes && detailRes.code === 200 && detailRes.data) {
          detailRows = detailRes.data.rows || []
        }
      }
      const company = (systemRes && systemRes.code === 200) ? (systemRes.data || {}) : {}
      this.printData = this.transformData(head, detailRows, company, qrData)
    },
    /** 转换为 SHD 模板打印数据 */
    transformData(head, detailRows, company, qrData) {
      const rows = (detailRows || []).map((row, index) => ({
        rowIndex: index + 1,
        barCode: row.barCode || '',
        name: row.name || '',
        standard: row.standard || '',
        model: row.model || '',
        unit: row.unit || '',
        operNumber: row.operNumber,
        unitPrice: row.unitPrice,
        allPrice: row.allPrice,
        remark: row.remark || ''
      }))
      return {
        billTitle: '送货单',
        number: qrData.xsckBillNo || head.number || '',
        linkNumber: qrData.xsddBillNo || head.linkNumber || '',
        operTimeStr: head.operTimeStr || '',
        organName: head.organName || '',
        contacts: head.contacts || '',
        telephone: head.telephone || '',
        address: head.address || '',
        remark: head.remark || '',
        companyName: company.companyName || '',
        companyAddress: company.companyAddress || '',
        companyTel: company.companyTel || '',
        qrContent: qrData.qrContent || '',
        scanTip: '客户扫码确认收货',
        table: rows,
        detailList: rows
      }
    },
    /** 加载 SHD 模板列表（设计器回显用） */
    async loadTemplateList() {
      try {
        const res = await getList({ billType: this.billTypeCode, size: 100 })
        if (res && res.code === 200 && res.data) {
          const list = res.data.rows || res.data || []
          const def = list.find(t => t.isDefault === 1) || list[0]
          this.selectedTemplateId = def ? def.id : null
        }
      } catch (e) {
        console.warn('[OrderCenterPrint] 模板列表加载失败:', e)
      }
    },
    /** 两级回退加载 SHD 模板并渲染 */
    async loadAndRender() {
      try {
        const userInfo = this.$store && (this.$store.getters.userInfo || (this.$store.state.user && this.$store.state.user.info))
        const tenantId = userInfo ? userInfo.tenantId : null
        const result = await loadTemplateWithFallback(this.billTypeCode, tenantId)
        if (result && result.templateJson && result.templateJson.panels) {
          this.templateJson = result.templateJson
        } else {
          this.templateJson = null
        }
        this.$nextTick(() => {
          this.renderTemplate()
        })
      } catch (error) {
        console.error('[OrderCenterPrint] 加载并渲染模板失败:', error)
      }
    },
    /** hiprint 渲染预览 */
    renderTemplate() {
      if (!this.templateJson || !this.$refs.printContent) {
        return
      }
      try {
        if (HiprintService.isAvailable()) {
          const html = HiprintService.renderHtml(this.templateJson, this.printData || {})
          if (html && html.trim()) {
            this.$refs.printContent.innerHTML = html
          }
        } else {
          console.warn('[OrderCenterPrint] hiprint 不可用')
        }
      } catch (error) {
        console.error('[OrderCenterPrint] 渲染模板失败:', error)
      }
    },
    /** 打印预览（新窗口打印） */
    handlePrintPreview() {
      const printContent = this.$refs.printContent
      if (!printContent || !printContent.innerHTML) {
        this.$message.warning('暂无内容可打印')
        return
      }
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        this.$message.error('无法打开打印窗口，请允许弹出窗口')
        return
      }
      printWindow.document.write(
        '<!DOCTYPE html><html><head><title>送货单 - ' + this.currentBillNo + '</title>' +
        '<style>body { margin: 0; padding: 20px; font-family: SimSun, "Microsoft YaHei", sans-serif; }' +
        '@media print { body { padding: 0; } }</style></head><body>' +
        printContent.innerHTML + '</body></html>')
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 500)
    },
    handleTemplateSetting() {
      this.designerVisible = true
    },
    onDesignerClose() {
      this.designerVisible = false
    },
    onDesignerSaved() {
      this.designerVisible = false
      this.loadTemplateList().then(() => this.loadAndRender())
    },
    handleCancel() {
      this.visible = false
      this.designerVisible = false
    }
  }
}
</script>
<style scoped>
.top-toolbar {
  padding: 8px 12px;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 8px;
}
.hiprint-container {
  min-height: 400px;
}
.preview-wrapper {
  overflow: auto;
  text-align: center;
}
.no-template {
  padding: 80px 0;
}
.designer-wrapper {
  height: calc(100% - 20px);
}
</style>
